(() => {
  'use strict';

  // The markets come from the one catalog of the theme (snippets/bestio-market-catalog.liquid), rendered into the
  // layout as JSON (BEST-284). Every Shopify market resolves (currency, phone prefix); whether the checkout serves it
  // is the Checkout API's answer (bestio-market-settings.js), not the theme's.
  // Without the catalog no market resolves, so the checkout shows its notice instead of guessing.
  function readCatalog() {
    try {
      const rows = JSON.parse(document.querySelector('[data-bestio-market-catalog]')?.textContent || '[]');
      return Object.freeze(Object.fromEntries(rows.map(row => [row.countryCode, Object.freeze({
        countryCode: row.countryCode, currencyCode: row.currencyCode, phonePrefix: row.phonePrefix,
        phoneExample: row.phoneExample, postalExample: row.postalExample,
      })])));
    } catch (error) {
      console.error('Bestio market catalog is not readable.', error);
      return Object.freeze({});
    }
  }
  const markets = readCatalog();

  function resolve(countryCode, currencyCode) {
    const normalizedCountry = String(countryCode || 'CZ').trim().toUpperCase();
    const market = markets[normalizedCountry];
    if (!market) throw new Error(`Shopify market ${normalizedCountry} is not supported by checkout.`);

    const normalizedCurrency = String(currencyCode || market.currencyCode).trim().toUpperCase();
    if (normalizedCurrency !== market.currencyCode) {
      throw new Error(`Shopify market ${normalizedCountry} must use ${market.currencyCode}, not ${normalizedCurrency}.`);
    }
    return market;
  }

  // Codes entered on the cart or in the checkout summary (`discount_codes`, only the applicable ones) plus
  // the cart-level applications the checkout already sent; one entry per code regardless of case.
  function discountCodes(cart) {
    const entered = (cart?.discount_codes || []).filter(discount => discount?.applicable !== false).map(discount => discount.code);
    const applied = (cart?.cart_level_discount_applications || []).map(discount => discount.title);
    const seen = new Set();
    return [...entered, ...applied].map(code => String(code || '').trim()).filter(code => {
      const key = code.toLowerCase();
      if (!code || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  // The visitor's consent with marketing as the Shopify cookie banner holds it now (Customer Privacy API, `marketingAllowed()`):
  // the consent the Google Ads tag fires with (bestio-analytics.js, `ad_storage`). The checkout keeps the last one it is sent and
  // records it on the order (`bestio.ad_measurement`, BEST-623, ADR 0028). Undefined while the banner's API is not loaded: an unread
  // banner says nothing about the choice, so the request leaves the field out and the checkout keeps what it knew.
  function marketingConsent() {
    try {
      const privacy = window.Shopify?.customerPrivacy;
      return typeof privacy?.marketingAllowed === 'function' ? privacy.marketingAllowed() === true : undefined;
    } catch (_) {
      return undefined;
    }
  }

  function createCartRequest(cart, market, languageCode) {
    const cartCurrency = String(cart?.currency || '').trim().toUpperCase();
    if (cartCurrency && cartCurrency !== market.currencyCode) {
      throw new Error(`Shopify cart currency ${cartCurrency} does not match market ${market.countryCode} (${market.currencyCode}).`);
    }

    const request = {
      lines: (cart?.items || []).map(item => ({lineKey: item.key, shopifyVariantId: String(item.variant_id), quantity: item.quantity})),
      discountCodes: discountCodes(cart),
      marketCode: market.countryCode,
      currencyCode: market.currencyCode,
      languageCode: String(languageCode || '').slice(0, 2).toLowerCase(),
    };
    // Where the customer came from, from the cart's attributes (bestio-visit-source.js, BEST-615). Without the script the field
    // is left out and the order's source is unknown; with it and no visit in the cart the checkout reads a direct visit.
    const visitSource = window.BestioVisitSource?.fromCart?.(cart);
    if (visitSource) request.visitSource = visitSource;
    const consent = marketingConsent();
    if (consent !== undefined) request.marketingConsent = consent;
    return request;
  }

  // The checkout pages under the routes root of the storefront's current locale (`Shopify.routes.root`: "/" or
  // "/en/"). Every way into the checkout goes through here: dropping the language subfolder would open the checkout in
  // the market's default language, and the checkout would create the order with that language.
  function checkoutPaths(routesRoot) {
    const root = String(routesRoot || '/').replace(/\/?$/, '/');
    return Object.freeze({
      checkout: `${root}pages/checkout-details`,
      thankYou: `${root}pages/checkout-thank-you`,
      cart: `${root}cart`,
    });
  }

  window.BestioCheckoutMarket = Object.freeze({markets, resolve, createCartRequest, checkoutPaths, marketingConsent});
})();
